import { styled } from '@/stitches.config';
import { DesktopMetadata, MetadataWrapper } from './desktop-metadata';
import { ImageCarousel } from './image-carousel';
import { useCaseTransitionStore } from '@/hooks/use-case-transition-store';
import { createTreemapFromPalette } from '@/utils/create-treemap-from-palette';
import { WorkProject } from '@/lib/schemas/work-project';
import { motion } from 'framer-motion';

type DesktopCardProps = {
  card: WorkProject;
  canAnimate: boolean;
};

export function DesktopCard(props: DesktopCardProps) {
  const display = props.card.display ? props.card.display : 'oneByOne';

  const { setCaseTransitionProps } = useCaseTransitionStore();

  return (
    <ListItem display={display}>
      <CardLink
        href={`/project/${props.card.target.slug.current}`}
        onClick={(event) => {
          event.preventDefault();
          const treemap = createTreemapFromPalette(
            props.card.target.cover.asset.metadata.palette
          );
          setCaseTransitionProps(
            treemap,
            `/project/${props.card.target.slug.current}`
          );
        }}
      >
        <Backdrop>
          <DesktopMetadata
            title={props.card.target.title}
            subtitle={props.card.target.subtitle}
          />
        </Backdrop>

        <BoxForPageTransition
          layoutId={`page-transition-box-${props.card.target._id}`}
          css={{
            position: 'relative',
            height: '100%',
            background:
              props.card.target.cover.asset.metadata.palette.vibrant.background,
          }}
        >
          <ImageCarousel canAnimate={props.canAnimate} project={props.card} />
        </BoxForPageTransition>
      </CardLink>
    </ListItem>
  );
}

const CardLink = styled('a', {
  display: 'block',
  position: 'absolute',
  inset: '$2',
  zIndex: '$default',

  [`&:is(:hover, :focus) ${MetadataWrapper}`]: {
    transform: 'translateY(0)',
  },

  '@bp4': {
    inset: '$4',
  },
});

const ListItem = styled('li', {
  position: 'relative',

  variants: {
    display: {
      oneByOne: {
        gridColumn: 'span 1',
        gridRow: 'span 1',
        aspectRatio: '1 / 1',
      },
      twoByOne: {
        gridColumn: 'span 2',
        gridRow: 'span 1',
        aspectRatio: '2 / 1',
      },
      oneByTwo: {
        gridColumn: 'span 1',
        gridRow: 'span 2',
        aspectRatio: '1 / 2',
      },
      twoByTwo: {
        gridColumn: 'span 2',
        gridRow: 'span 2',
        aspectRatio: '1 / 1',
      },
    },
  },
  defaultVariants: {
    display: 'oneByOne',
  },
});

const Backdrop = styled('div', {
  position: 'absolute',
  inset: '0',
  zIndex: '$textOverlay',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  flexDirection: 'column',
  overflow: 'hidden',
  background: `linear-gradient(to top,
        rgba(0, 0, 0, 0.5) 0%,
        rgba(0, 0, 0, 0.3) 18%,
        rgba(0, 0, 0, 0.1) 40%,
        rgba(0, 0, 0, 0.0) 70%,
        rgba(255, 255, 255, 0) 100%)
      `,
});

const BoxForPageTransition = styled(motion.div, {
  display: 'block',
  position: 'absolute',
  inset: 0,
  zIndex: 0,
});
